import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TOOL_TYPES } from '../../store/useToolStore';
import { TOOL_SLUGS } from '../../data/toolSlugs';

const getToolPath = (toolType) => {
  return TOOL_SLUGS.find((t) => t.toolType === toolType)?.seoPath || '/';
};

const TOOL_ROUTES = {
  [TOOL_TYPES.LOADER]: { path: getToolPath(TOOL_TYPES.LOADER), icon: '⚙️', name: 'Loaders' },
  [TOOL_TYPES.GRADIENT]: { path: getToolPath(TOOL_TYPES.GRADIENT), icon: '🌈', name: 'Gradients' },
  [TOOL_TYPES.CARD]: { path: getToolPath(TOOL_TYPES.CARD), icon: '🪪', name: 'Cards' },
  [TOOL_TYPES.ANIMATION]: { path: getToolPath(TOOL_TYPES.ANIMATION), icon: '⚡', name: 'Animations' },
  [TOOL_TYPES.ALERT]: { path: getToolPath(TOOL_TYPES.ALERT), icon: '🔔', name: 'Alerts' },
  [TOOL_TYPES.PHOTO_PICKER]: { path: getToolPath(TOOL_TYPES.PHOTO_PICKER), icon: '📸', name: 'Photo Picker' },
  [TOOL_TYPES.SHADOW]: { path: getToolPath(TOOL_TYPES.SHADOW), icon: '🕶️', name: 'Shadows' },
  [TOOL_TYPES.BUTTON]: { path: getToolPath(TOOL_TYPES.BUTTON), icon: '🔘', name: 'Buttons' },
  [TOOL_TYPES.GRID]: { path: getToolPath(TOOL_TYPES.GRID), icon: '🧩', name: 'Grids' },
  [TOOL_TYPES.PALETTE]: { path: getToolPath(TOOL_TYPES.PALETTE), icon: '🎨', name: 'Palettes' },
  [TOOL_TYPES.JSON_TO_MODEL]: { path: getToolPath(TOOL_TYPES.JSON_TO_MODEL), icon: '🔍', name: 'JSON → Model' },
  [TOOL_TYPES.API_GENERATOR]: { path: getToolPath(TOOL_TYPES.API_GENERATOR), icon: '🌐', name: 'API Generator' },
  [TOOL_TYPES.STORYBOARD_CONVERTER]: { path: getToolPath(TOOL_TYPES.STORYBOARD_CONVERTER), icon: '🧰', name: 'Storyboard → SwiftUI' },
  [TOOL_TYPES.POSTMAN_IMPORTER]: { path: getToolPath(TOOL_TYPES.POSTMAN_IMPORTER), icon: '📝', name: 'Postman Import' },
};

const Toolbar = () => {
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);

  const isActive = (path) => location.pathname === path;
  
  useEffect(() => {
    setShowMore(false);
  }, [location.pathname]);

  const navToolTypes = Object.keys(TOOL_ROUTES)
    .filter((t) => t !== TOOL_TYPES.STORYBOARD_CONVERTER);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
      <div className="max-w-[1400px] mx-auto px-2 sm:px-4 py-2 sm:py-3">
        {/* Desktop: Horizontal Scroll */}
        <div className="hidden md:block">
        {/* Tools Scroll */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {navToolTypes.map((type) => {
            const tool = TOOL_ROUTES[type];
            return (
              <Link
                key={type}
                to={tool.path}
                className={`
                  flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all whitespace-nowrap text-sm
                  ${
                    isActive(tool.path)
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                <span className="text-base sm:text-lg">{tool.icon}</span>
                <span className="font-medium hidden sm:inline">{tool.name}</span>
              </Link>
            );
          })}
        </div>
        </div>

        {/* Mobile: Dropdown */}
        <div className="md:hidden">
          <button
            onClick={() => setShowMore(!showMore)}
            className="w-full flex items-center justify-between px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            <span className="font-medium">Select Tool</span>
            <svg className={`w-5 h-5 transition-transform ${showMore ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showMore && (
            <div className="mt-2 space-y-1">
              {navToolTypes.map((type) => {
                const tool = TOOL_ROUTES[type];
                return (
                  <Link
                    key={type}
                    to={tool.path}
                    onClick={() => setShowMore(false)}
                    className={`
                      flex items-center gap-3 px-4 py-2 rounded-lg transition-colors
                      ${
                        isActive(tool.path)
                          ? 'bg-gradient-primary text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    <span className="text-xl">{tool.icon}</span>
                    <span className="font-medium">{tool.name}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Toolbar;

